import { safeSessionStorage } from '../utils/safeStorage';

// After this many failed repairs we stop pretending another identical retry will help and hand the
// borrower a way to reach a human.
const MAX_RECONNECT_ATTEMPTS = 3;

// The count rides in the URL rather than in Web Storage. Every retry is a full page load (see
// retryUpdateMode), so the count has to survive navigation — and sessionStorage cannot be relied on
// to do that: in private mode and in-app webviews the storage getter itself throws (#5873) and the
// safe wrapper degrades to an in-memory Map that a reload discards. Since a page instance can only
// ever record ONE failure (after that the sole control is Try Again, which navigates), a
// storage-backed count in those browsers never passes 1 and the cap would never trip for exactly the
// borrowers most likely to be stuck. A query param has no such failure mode.
const ATTEMPT_PARAM = 'attempt';
// The KIND of the last failure rides alongside the count. Without it a reload downgraded a blocked
// SDK to the generic "please try again" — the one piece of advice that borrower must not be given —
// and dropped the support route, because that is keyed on the kind rather than the count.
const REASON_PARAM = 'reason';
// Only kinds that change what the borrower is told exist. A third "reconnect" value was tried and
// removed: it round-tripped through the URL and rendered identically to no reason at all, so it was
// noise that two counted paths set inconsistently.
const REASON_LINK_OPEN = 'link';
const REASON_TIMEOUT = 'timeout';
// Only the blocked-SDK diagnosis survives a reload. A timeout's whole content is an unresolved
// "it may still have reconnected" — and the reload it recommends resolves it, because updateMode()
// redirects to the dashboard when the connection is healthy. Carrying it meant the page that can
// only exist BECAUSE the repair failed still told the borrower it might have worked.
const PERSISTED_REASONS = [REASON_LINK_OPEN];
const UPDATE_MODE_PATH = '/account/update-mode';

// The URL is the primary carrier, but it is not the only way back onto this page: LoanController's
// redirect and the PlaidLoginRequiredAlert email both route here with a CLEAN url. A borrower who
// fails twice and then taps their loan or re-opens the email would arrive at attempt 0 and could
// loop forever without ever being offered a human. So the count is mirrored here as well and the two
// are reconciled on mount. Storage alone would not do — in private mode and in-app webviews it
// degrades to an in-memory Map a reload discards, which is why the URL leads.
const ATTEMPTS_KEY = 'plaid-update-mode-attempts';
// Aged out so a connection repaired out of band — which never passes through proceed() — does not
// leave a stale count that trips the cap early on an unrelated break weeks later.
const ATTEMPTS_TTL_MS = 30 * 60 * 1000;

// A hung verification POST used to strand the borrower on the "Verifying" panel forever. axios has
// no default timeout, so this bounds it. Deliberately generous: updateModeOk makes several SEQUENTIAL
// blocking Plaid calls and Laravel's HTTP client allows each one 30s on its own, so a budget near 30s
// would abort repairs that are still progressing — and PHP does not abort with us, so the repair
// would commit server-side while the borrower was told it failed.
const VERIFY_TIMEOUT_MS = 120000;

// Telemetry must never carry the raw error object. axios attaches an enumerable toJSON() that
// serialises `config` — which holds the request BODY (the Plaid public_token) and the request
// HEADERS (the CSRF token) — and reportLinkEvent's payload is stored verbatim in a json column.
// Reading the fields explicitly also fixes plain Errors, whose message/stack are non-enumerable and
// would otherwise serialise to `{}`.
function describeError(error) {
    if (!error) {
        return null;
    }

    return {
        name: error.name ?? null,
        message: error.error_message ?? error.message ?? null,
        code: error.code ?? error.error_code ?? null,
        type: error.error_type ?? null,
        // The one diagnostic that matters for a blocked-SDK ReferenceError, and the one that tells a
        // 419 from a 500. Neither is sensitive.
        status: error.response?.status ?? null,
        stack: error.stack ?? null,
    };
}

export default {
    // Deliberately NO auto-open on mount. It was tried: opening Link for a borrower arriving with a
    // carried count saved them a click, but handleUpdateMode() latches `loading`, and the failure UI
    // was at the time gated on it — so the whole thing rendered for one frame and vanished on
    // exactly the pages that carry a failure count. (Both paragraphs are gated on `verifying` now,
    // so that specific symptom would not recur, but the rest below still stands.) It also
    // fired on any reload/back/bookmark of a `?attempt=` URL (countFailedAttempt rewrites the bar
    // via replaceState), opening a third-party modal with no gesture and inflating both the cap and
    // the telemetry. The button on that page is labelled "Try Again" and opens Link in one click.
    created() {
        // Whichever carrier knows more wins: the URL after a Try Again, storage after a query-less
        // re-entry from elsewhere in the app.
        this.failedAttempts = Math.max(this.attemptsFromUrl(), this.rememberedAttempts());
        // A kind is meaningless without a failure to attach it to: `?reason=link` alone would
        // otherwise render the support route on a page where nothing has gone wrong.
        this.failureKind = this.failedAttempts > 0 ? this.reasonFromUrl() : '';
    },

    computed: {
        // Derived, never assigned: there is no second copy of this to drift out of sync with.
        reconnectExhausted() {
            return this.failedAttempts >= MAX_RECONNECT_ATTEMPTS;
        },

        linkOpenFailed() {
            return this.failureKind === REASON_LINK_OPEN;
        },

        // Gated on `verifying`, the same gate the support route uses — so the support route can
        // never appear without this line above it. (The reverse is normal: at `?attempt=1` this
        // renders alone, since the support route also needs the cap or a blocked SDK.) Only the
        // verification POST warrants silence: claiming "please try again" under "Please wait while
        // we verify your bank account" is the contradiction worth suppressing. During a Link session or a retry navigation the carried-over explanation is
        // still true, and hiding it there orphaned the support line beneath it. The specific wordings
        // cannot go stale over a running session either, because handleUpdateMode() clears
        // `failureKind` before Link opens.
        //
        // Both terms, deliberately. `failedAttempts > 0` alone is sufficient TODAY — every path that
        // sets `reconnectFailed` also calls countFailedAttempt() — but that is an invariant a future
        // path could break silently, and the failure mode is the Try Again button rendering with no
        // explanation above it. Cheap to keep, and pinned by a test.
        showReconnectError() {
            return !this.verifying && (this.reconnectFailed || this.failedAttempts > 0);
        },

        // "please try again" is only honest advice when trying again could work. If Link itself
        // could not be opened — a blocked CDN script, an extension, a locked-down webview — another
        // click provably cannot help, so say what actually happened instead.
        reconnectErrorMessage() {
            if (this.linkOpenFailed) {
                return "We couldn't open the bank connection window. A browser extension or network restriction is usually the cause.";
            }

            if (this.failureKind === REASON_TIMEOUT) {
                return "Your bank is taking longer than expected to respond. It may still have reconnected \u2014 try again to check.";
            }

            return "Your bank isn't reconnected yet and your loan is still on hold \u2014 please try again.";
        },

        showSupportRoute() {
            // Offered at the cap, and immediately when Link cannot open at all — that borrower will
            // never get there by retrying, so making them spend three attempts first is pointless.
            // No href means no human to hand them to, and an inert sentence is worse than silence.
            //
            // Same gate as showReconnectError, so this never stands alone. It also must survive
            // `retryUpdateMode`'s `loading` latch: removing the escape hatch for the seconds a
            // reload takes removed it exactly when a stuck borrower was reaching for it.
            return !this.verifying
                && (this.reconnectExhausted || this.linkOpenFailed)
                && !!this.supportHref;
        },

        mailtoSupport() {
            const email = window?.app?.support_email ?? window?.support_email ?? null;

            return email ? `mailto:${email}` : null;
        },

        // Last resort when no support address is configured: an inert "Contact support" sentence is
        // no use in the one state whose whole purpose is handing the borrower a human.
        telSupport() {
            const phone = window?.app?.company_phone ?? null;

            return phone ? `tel:${phone.replace(/[^+\d]/g, '')}` : null;
        },

        supportHref() {
            return this.mailtoSupport ?? this.telSupport ?? window?.app?.contact_us_url ?? null;
        },
    },

    methods: {
        handleUpdateMode() {
            if (this.loading) {
                return;
            }

            this.loading = true;
            this.verifying = false;
            // Whatever the last page diagnosed, we are about to find out afresh — and the URL has to
            // forget it too, or cancelling a Link session that opened perfectly well leaves a reload
            // still blaming a blocked SDK.
            this.failureKind = '';
            this.syncAttemptUrl();

            // `Plaid` is a global from the CDN script tag in layouts/app.blade.php, which the layout
            // itself treats as fallible (`onerror="window.__plaidSdkFailed = true"`). Without this
            // guard a missing SDK throws straight out of the click handler with `loading` already
            // latched true, and AppButton (disabled whenever it is loading) leaves the borrower on a
            // dead spinner with no way back.
            try {
                const linkHandler = Plaid.create({
                    token: this.link,
                    onSuccess: (public_token, metadata) => {
                        // `verifying` starts HERE, not at the click. It drives the "Verifying Bank
                        // Account / Please wait" panel, and until Link hands back a public token
                        // there is nothing to verify — the borrower is still choosing their bank and
                        // typing credentials over the top of this page.
                        this.verifying = true;

                        this.$http
                            .post(`/account/update-mode-ok`, {
                                public_token,
                                institution: metadata.institution,
                                accounts: metadata.accounts,
                            }, { timeout: VERIFY_TIMEOUT_MS }).then((response) => {
                                // `updateModeOk` answers 200 even when the repair failed; `reconnected`
                                // is the only signal that the bank is actually connected again.
                                if (response?.data?.reconnected) {
                                    this.proceed();
                                    return;
                                }

                                this.failReconnect('UPDATE_MODE_NOT_RECONNECTED', metadata);
                            }).catch((error) => {
                                // A timeout is not evidence of failure: we stopped listening, the
                                // server did not stop working, so the repair may have committed.
                                // Surface it and offer the retry — which reloads, and updateMode()
                                // then redirects to the dashboard if the bank is in fact healthy —
                                // but do not spend one of the three cap slots on a maybe.
                                // axios 0.21 reuses ECONNABORTED for a deliberate abort (xhr.js:89)
                                // as well as a timeout (:114), so the code alone is not a
                                // discriminator — only the timeout carries this message.
                                if (error?.code === 'ECONNABORTED' && /timeout/i.test(error?.message ?? '')) {
                                    this.failVerifyTimeout(metadata, error);
                                    return;
                                }

                                this.failReconnect('UPDATE_MODE_POST_FAILED', metadata, error);
                            });
                    },
                    onEvent: (event, metadata) => {
                        window.axios?.post('/api/plaid/link-events', {
                            event_name: event,
                            loan_id: this.loan?.id ?? null,
                            institution_name: metadata.institution_name ?? metadata.institution?.name ?? null,
                            error_type: metadata.error_type ?? null,
                            error_code: metadata.error_code ?? null,
                            error_message: metadata.error_message ?? null,
                            link_session_id: metadata.link_session_id ?? null,
                            request_id: metadata.request_id ?? null,
                            exit_status: metadata.exit_status ?? null,
                            metadata: metadata,
                            event_time: new Date().toISOString(),
                        }).catch(() => {});
                    },
                    onExit: (err, metadata) => {
                        // The user exited the Link flow.
                        // A deliberate cancel is not an error: nothing is claimed, nothing counted,
                        // and the page simply returns to whatever it was already saying.
                        this.loading = false;
                        this.verifying = false;

                        if (err != null) {
                            // The user encountered a Plaid API error prior
                            // to exiting.
                            // A real Plaid error — institution down, expired update-mode token, a
                            // recurring ITEM_LOGIN_REQUIRED — is a failed attempt, not a change of
                            // mind, and clicking again will not fix it. A plain cancel stays
                            // uncounted; this must not, or the cap never trips for the borrower it
                            // exists to rescue.
                            // No kind: this failure gets the generic wording. handleUpdateMode()
                            // cleared it at the click and only failLinkOpen sets one before Link
                            // opens — which precludes onExit — so it is already '' here.
                            this.countFailedAttempt();
                            // Same as every other counted failure: route the borrower to a reload.
                            // Re-opening Link here would reuse the token this session just errored
                            // on, which for INVALID_LINK_TOKEN / expiry is a guaranteed dead loop.
                            this.reconnectFailed = true;
                            this.reportLinkEvent('UPDATE_MODE_LINK_EXIT_ERROR', metadata, err);
                        }
                        // metadata contains the most recent API request ID and the
                        // Link session ID. Storing this information is helpful
                        // for support.
                    },
                });

                linkHandler.open();
            } catch (error) {
                this.failLinkOpen(error);
            }
        },

        failVerifyTimeout(metadata, error) {
            this.loading = false;
            this.verifying = false;
            this.reconnectFailed = true;
            this.failureKind = REASON_TIMEOUT;

            // Counted. Not counting it read well — we genuinely do not know the repair failed — but it
            // meant a borrower whose POST times out every time could never reach the cap, and so never
            // reached the support route: the one escape hatch, withheld from someone plainly stuck.
            // The copy carries the uncertainty instead of the counter.
            this.countFailedAttempt();

            this.reportLinkEvent('UPDATE_MODE_VERIFY_TIMEOUT', metadata, error);
        },

        failLinkOpen(error) {
            this.loading = false;
            this.verifying = false;
            this.failureKind = REASON_LINK_OPEN;

            // Counts against the cap. A borrower whose Plaid script is blocked — ad blocker, CSP,
            // in-app webview — can never succeed by clicking again, so this is the path that most
            // needs to reach the support route.
            //
            // `reconnectFailed` is set so THIS page's control reloads rather than re-running the
            // same in-page code, which is provably futile while the script is blocked. The page the
            // reload serves deliberately offers an in-page attempt again: a fresh document is the
            // one thing that can produce a working SDK, so it has to be allowed to try.
            this.reconnectFailed = true;
            this.countFailedAttempt();

            this.reportLinkEvent('UPDATE_MODE_LINK_OPEN_FAILED', null, error);
        },

        failReconnect(eventName, metadata, error) {
            this.loading = false;
            this.verifying = false;
            this.reconnectFailed = true;
            // No kind: a failed repair gets the generic wording. Already '' here — handleUpdateMode()
            // cleared it at the click and this runs inside onSuccess, so nothing could have set one.

            // Some failures are deterministic — a loan on manual banking, for instance, can never be
            // repaired by this endpoint, so "please try again" is advice that can never come true.
            // Past the cap we keep the retry available but add a route to a human beside it.
            this.countFailedAttempt();

            this.reportLinkEvent(eventName, metadata, error);
        },

        countFailedAttempt() {
            this.failedAttempts += 1;
            this.syncAttemptUrl();
            this.rememberAttempts();
        },

        attemptsKey() {
            // Per loan: an agent moving between borrowers in one tab must not inherit a count.
            return `${ATTEMPTS_KEY}:${this.loan?.id ?? 'unknown'}`;
        },

        rememberAttempts() {
            safeSessionStorage.setItem(
                this.attemptsKey(),
                JSON.stringify({ count: this.failedAttempts, at: Date.now() }),
            );
        },

        rememberedAttempts() {
            const stored = safeSessionStorage.getItem(this.attemptsKey());

            if (!stored) {
                return 0;
            }

            try {
                const { count, at } = JSON.parse(stored);

                if (!Number.isFinite(count) || !Number.isFinite(at) || Date.now() - at > ATTEMPTS_TTL_MS) {
                    return 0;
                }

                return count;
            } catch (error) {
                return 0;
            }
        },

        forgetAttempts() {
            safeSessionStorage.removeItem(this.attemptsKey());
        },

        // Keeps the address bar in step with BOTH the count and the kind, so a borrower who hits F5
        // or back/forward re-reads the truth — a reason left there after it stopped being true is a
        // page that confidently states the wrong cause.
        //
        // retryUpdateMode() also DEPENDS on this: reload() re-requests whatever is in the bar, so a
        // failed sync would silently reset the count and put the cap out of reach. Hence the boolean,
        // which lets that caller fall back to carrying the state in the navigation instead.
        //
        // The try/catch is what keeps a throw here from breaking the caller that is mid-way through
        // recording a failure. The sharpest case is the errored-onExit path: a throw out of
        // countFailedAttempt() would skip the `reconnectFailed = true` immediately after it, leaving
        // the button bound to handleUpdateMode — which re-opens Link with the token that just errored,
        // the dead loop that path exists to avoid — and would drop the telemetry row with it.
        syncAttemptUrl() {
            try {
                // No optional chaining: a missing History API should land in the catch alongside a
                // throwing one, so both degrade to the same `false` and the same fallback.
                window.history.replaceState({}, '', this.attemptUrl());

                return true;
            } catch (error) {
                return false;
            }
        },

        attemptUrl({ includeHash = true } = {}) {
            // Built from the CURRENT query string rather than from scratch. syncAttemptUrl() replaces
            // the real address bar on every click, so rebuilding it from a bare path silently dropped
            // anything else the borrower arrived with — a tracking param on the email link, an
            // anchor. Nothing links here with extra params today; this keeps that from becoming a
            // silent breakage the first time something does.
            const query = new URLSearchParams(window?.location?.search ?? '');

            query.delete(ATTEMPT_PARAM);
            query.delete(REASON_PARAM);

            if (this.failedAttempts > 0) {
                query.set(ATTEMPT_PARAM, String(this.failedAttempts));

                if (PERSISTED_REASONS.includes(this.failureKind)) {
                    query.set(REASON_PARAM, this.failureKind);
                }
            }

            const search = query.toString();

            const hash = includeHash ? (window?.location?.hash ?? '') : '';

            return `${UPDATE_MODE_PATH}${search ? `?${search}` : ''}${hash}`;
        },

        reasonFromUrl() {
            const reason = new URLSearchParams(window?.location?.search ?? '').get(REASON_PARAM);

            // Anything hand-edited — or any kind we deliberately do not persist — falls back to the
            // generic message rather than being echoed.
            return PERSISTED_REASONS.includes(reason) ? reason : '';
        },

        attemptsFromUrl() {
            const raw = new URLSearchParams(window?.location?.search ?? '').get(ATTEMPT_PARAM);
            const attempts = Number.parseInt(raw ?? '0', 10);

            // A hand-edited param is harmless — the worst it can do is offer the support link early —
            // but it must not produce NaN and poison every comparison downstream.
            // parseInt yields a number or NaN, and NaN fails `> 0`, so this is the whole guard.
            return attempts > 0 ? attempts : 0;
        },

        reportLinkEvent(eventName, metadata, error) {
            const details = metadata ?? {};

            window.axios?.post('/api/plaid/link-events', {
                event_name: eventName,
                loan_id: this.loan?.id ?? null,
                institution_name: details.institution_name ?? details.institution?.name ?? null,
                // Plaid's onExit hands the error as its FIRST argument, a plain object carrying
                // error_type/error_code/error_message and no `.message`. Reading only `metadata`
                // (and only `.message`) wrote a row with every error field null.
                error_type: error?.error_type ?? details.error_type ?? null,
                error_code: error?.error_code ?? error?.code ?? details.error_code ?? null,
                error_message: error?.error_message ?? error?.message ?? details.error_message ?? null,
                link_session_id: details.link_session_id ?? null,
                request_id: details.request_id ?? null,
                exit_status: details.exit_status ?? null,
                metadata: error ? { ...details, error: describeError(error) } : details,
                event_time: new Date().toISOString(),
            }).catch(() => {});
        },

        retryUpdateMode() {
            // `loading` is set so the Try Again button spins for the several seconds this navigation
            // takes: /account/update-mode blocks on Plaid::getIdentity() and getLinkTokenUpdateMode()
            // before it renders, and an inert button invites repeated taps.
            this.loading = true;

            // Re-assert the count and kind, then force a real document fetch. This used to assign
            // `location.href = attemptUrl()`, which is a NO-OP whenever the URL carries a fragment:
            // syncAttemptUrl has already made the address bar equal that exact string, and assigning
            // a URL that matches the current one with a non-null fragment is a fragment navigation —
            // scroll only, no fetch. The borrower was left on a latched spinner with no other
            // control, since the template renders only the retry button in that state.
            //
            // reload() re-requests the current URL, so the count, the kind, the fragment and any
            // foreign params all survive, and there is no same-URL fast path to fall into. We need a
            // reload rather than an in-page retry because updateMode() mints `this.link` once per
            // page load, and an already-consumed update-mode token is not reliable to re-open.
            if (this.syncAttemptUrl()) {
                window.location.reload();

                return;
            }

            // The sync did not land, so the bar does not carry this failure and a reload would lose
            // it. Fall back to putting the state in the navigation itself — what this did before the
            // reload change — minus the fragment, since assigning a URL that matches the current one
            // apart from nothing at all is the same-document fragment jump that made the reload
            // necessary in the first place.
            window.location.href = this.attemptUrl({ includeHash: false });
        },

        proceed() {
            // `verifying` and `loading` deliberately stay true so the "Verifying Bank Account" block
            // and its spinner remain on screen while the browser navigates. `verifying` staying true
            // is also what keeps the failure copy off the screen for the whole redirect.
            this.forgetAttempts();
            window.location.href = `/account/loans/${this.loan.id}`;
        },
    }
}
